import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  Fab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  LinearProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  FilterList,
  Refresh,
  Visibility,
  Download,
  Print,
  ExpandMore,
  ShoppingCart,
  Person,
  Payment,
  LocalShipping,
  CheckCircle,
  Warning,
  Error,
  TrendingUp,
  TrendingDown,
  Business,
  Assessment,
  Settings,
  AddShoppingCart,
  RemoveShoppingCart,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRbac } from '../../context/RbacContext';
import { useCreateWorkOrderFromOrderMutation } from '../../api/productionFlowApi';
import { Engineering } from '@mui/icons-material';

interface Order {
  id: string;
  orderNumber: string;
  customerId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  status: 'pending' | 'confirmed' | 'in_production' | 'ready' | 'shipped' | 'delivered' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  totalAmount: number;
  items: OrderItem[];
  paymentStatus: 'pending' | 'partial' | 'paid' | 'refunded';
  paymentMethod: 'mpesa' | 'bank' | 'card' | 'cash';
  createdAt: string;
  updatedAt: string;
  expectedDelivery: string;
  actualDelivery?: string;
  notes: string;
}

interface OrderItem {
  id: string;
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  specifications: string;
}

interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  company: string;
  isActive: boolean;
}

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  isActive: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`order-tabpanel-${index}`}
      aria-labelledby={`order-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const OrderManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/manager') ? '/manager' : '/admin';
  const { canManage } = useRbac();
  const [createWorkOrder, { isLoading: creatingWO }] = useCreateWorkOrderFromOrderMutation();
  const [tabValue, setTabValue] = useState(0);
  const [orders, setOrders] = useState<Order[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [viewingOrder, setViewingOrder] = useState<Order | null>(null);
  const [dialogType, setDialogType] = useState<'order' | 'customer' | 'product'>('order');

  // Form states
  const [orderForm, setOrderForm] = useState({
    customerId: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    expectedDelivery: '',
    notes: '',
    items: [] as OrderItem[],
  });

  const [customerForm, setCustomerForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    company: '',
  });

  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: 0,
    category: '',
  });

  // Mock data
  useEffect(() => {
    setOrders([
      {
        id: '1',
        orderNumber: 'ORD-2024-001',
        customerId: '1',
        customerName: 'John Doe',
        customerEmail: 'john@example.com',
        customerPhone: '+254712345678',
        status: 'confirmed',
        priority: 'high',
        totalAmount: 15000,
        items: [
          {
            id: '1',
            productId: '1',
            productName: 'Custom T-Shirt',
            quantity: 50,
            unitPrice: 300,
            totalPrice: 15000,
            specifications: 'Cotton, Blue, Size M',
          },
        ],
        paymentStatus: 'paid',
        paymentMethod: 'mpesa',
        createdAt: '2024-01-15T10:30:00Z',
        updatedAt: '2024-01-15T10:30:00Z',
        expectedDelivery: '2024-01-25T00:00:00Z',
        notes: 'Rush order for corporate event',
      },
      {
        id: '2',
        orderNumber: 'ORD-2024-002',
        customerId: '2',
        customerName: 'Jane Smith',
        customerEmail: 'jane@example.com',
        customerPhone: '+254798765432',
        status: 'pending',
        priority: 'medium',
        totalAmount: 25000,
        items: [
          {
            id: '2',
            productId: '2',
            productName: 'Leather Bag',
            quantity: 10,
            unitPrice: 2500,
            totalPrice: 25000,
            specifications: 'Brown leather, Large size',
          },
        ],
        paymentStatus: 'pending',
        paymentMethod: 'bank',
        createdAt: '2024-01-14T15:45:00Z',
        updatedAt: '2024-01-14T15:45:00Z',
        expectedDelivery: '2024-01-30T00:00:00Z',
        notes: 'Standard delivery',
      },
    ]);

    setCustomers([
      {
        id: '1',
        name: 'John Doe',
        email: 'john@example.com',
        phone: '+254712345678',
        address: 'Nairobi, Kenya',
        company: 'Tech Corp',
        isActive: true,
      },
      {
        id: '2',
        name: 'Jane Smith',
        email: 'jane@example.com',
        phone: '+254798765432',
        address: 'Mombasa, Kenya',
        company: 'Fashion Ltd',
        isActive: true,
      },
    ]);

    setProducts([
      {
        id: '1',
        name: 'Custom T-Shirt',
        description: 'High-quality cotton t-shirt with custom printing',
        price: 300,
        category: 'clothing',
        isActive: true,
      },
      {
        id: '2',
        name: 'Leather Bag',
        description: 'Handcrafted leather bag',
        price: 2500,
        category: 'accessories',
        isActive: true,
      },
    ]);
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenDialog = (type: 'order' | 'customer' | 'product', item?: any) => {
    setDialogType(type);
    setEditingOrder(item || null);
    setOpenDialog(true);
    setError(null);

    if (item) {
      if (type === 'order') {
        setOrderForm({
          customerId: item.customerId,
          priority: item.priority,
          expectedDelivery: item.expectedDelivery,
          notes: item.notes,
          items: item.items,
        });
      } else if (type === 'customer') {
        setCustomerForm({
          name: item.name,
          email: item.email,
          phone: item.phone,
          address: item.address,
          company: item.company,
        });
      } else if (type === 'product') {
        setProductForm({
          name: item.name,
          description: item.description,
          price: item.price,
          category: item.category,
        });
      }
    } else {
      // Reset forms for new items
      if (type === 'order') {
        setOrderForm({
          customerId: '',
          priority: 'medium',
          expectedDelivery: '',
          notes: '',
          items: [],
        });
      } else if (type === 'customer') {
        setCustomerForm({
          name: '',
          email: '',
          phone: '',
          address: '',
          company: '',
        });
      } else if (type === 'product') {
        setProductForm({
          name: '',
          description: '',
          price: 0,
          category: '',
        });
      }
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingOrder(null);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!canManage('manager:orders') && !canManage('admin:orders')) {
      setError('You do not have permission to manage orders');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (dialogType === 'order') {
        if (!orderForm.customerId || orderForm.items.length === 0) {
          setError('Please select a customer and add items');
          return;
        }

        if (editingOrder) {
          // Update order
          const updatedOrders = orders.map(order =>
            order.id === editingOrder.id
              ? {
                  ...order,
                  customerId: orderForm.customerId,
                  priority: orderForm.priority,
                  expectedDelivery: orderForm.expectedDelivery,
                  notes: orderForm.notes,
                  items: orderForm.items,
                  totalAmount: orderForm.items.reduce((sum, item) => sum + item.totalPrice, 0),
                  updatedAt: new Date().toISOString(),
                }
              : order
          );
          setOrders(updatedOrders);
        } else {
          // Create order
          const customer = customers.find(c => c.id === orderForm.customerId);
          const newOrder: Order = {
            id: Date.now().toString(),
            orderNumber: `ORD-${new Date().getFullYear()}-${String(orders.length + 1).padStart(3, '0')}`,
            customerId: orderForm.customerId,
            customerName: customer?.name || '',
            customerEmail: customer?.email || '',
            customerPhone: customer?.phone || '',
            status: 'pending',
            priority: orderForm.priority,
            totalAmount: orderForm.items.reduce((sum, item) => sum + item.totalPrice, 0),
            items: orderForm.items,
            paymentStatus: 'pending',
            paymentMethod: 'mpesa',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            expectedDelivery: orderForm.expectedDelivery,
            notes: orderForm.notes,
          };
          setOrders([newOrder, ...orders]);
        }
      } else if (dialogType === 'customer') {
        if (!customerForm.name || !customerForm.email) {
          setError('Please fill in all required fields');
          return;
        }

        if (editingOrder) {
          // Update customer
          const updatedCustomers = customers.map(customer =>
            customer.id === editingOrder.id
              ? { ...customer, ...customerForm }
              : customer
          );
          setCustomers(updatedCustomers);
        } else {
          // Create customer
          const newCustomer: Customer = {
            id: Date.now().toString(),
            ...customerForm,
            isActive: true,
          };
          setCustomers([...customers, newCustomer]);
        }
      } else if (dialogType === 'product') {
        if (!productForm.name || !productForm.description) {
          setError('Please fill in all required fields');
          return;
        }

        if (editingOrder) {
          // Update product
          const updatedProducts = products.map(product =>
            product.id === editingOrder.id
              ? { ...product, ...productForm }
              : product
          );
          setProducts(updatedProducts);
        } else {
          // Create product
          const newProduct: Product = {
            id: Date.now().toString(),
            ...productForm,
            isActive: true,
          };
          setProducts([...products, newProduct]);
        }
      }

      handleCloseDialog();
    } catch (err) {
      setError('Failed to save item');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type: 'order' | 'customer' | 'product', id: string) => {
    if (!canManage('manager:orders') && !canManage('admin:orders')) {
      setError('You do not have permission to delete items');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (type === 'order') {
        setOrders(orders.filter(order => order.id !== id));
      } else if (type === 'customer') {
        setCustomers(customers.filter(customer => customer.id !== id));
      } else if (type === 'product') {
        setProducts(products.filter(product => product.id !== id));
      }
    } catch (err) {
      setError('Failed to delete item');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId: string, newStatus: Order['status']) => {
    if (!canManage('manager:orders') && !canManage('admin:orders')) {
      setError('You do not have permission to update orders');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const updatedOrders = orders.map(order =>
        order.id === orderId
          ? {
              ...order,
              status: newStatus,
              updatedAt: new Date().toISOString(),
              actualDelivery: newStatus === 'delivered' ? new Date().toISOString() : order.actualDelivery,
            }
          : order
      );
      setOrders(updatedOrders);
    } catch (err) {
      setError('Failed to update order status');
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerEmail.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || order.priority === priorityFilter;
    const matchesPayment = paymentFilter === 'all' || order.paymentStatus === paymentFilter;

    return matchesSearch && matchesStatus && matchesPriority && matchesPayment;
  });

  const stats = {
    totalOrders: orders.length,
    pendingOrders: orders.filter(o => o.status === 'pending').length,
    confirmedOrders: orders.filter(o => o.status === 'confirmed').length,
    inProductionOrders: orders.filter(o => o.status === 'in_production').length,
    completedOrders: orders.filter(o => o.status === 'delivered').length,
    totalRevenue: orders.reduce((sum, o) => sum + o.totalAmount, 0),
    pendingPayments: orders.filter(o => o.paymentStatus === 'pending').length,
    totalCustomers: customers.length,
    totalProducts: products.length,
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'confirmed':
        return 'info';
      case 'in_production':
        return 'primary';
      case 'ready':
        return 'success';
      case 'shipped':
        return 'secondary';
      case 'delivered':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Order Management
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Orders
              </Typography>
              <Typography variant="h4">{stats.totalOrders}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending
              </Typography>
              <Typography variant="h4" color="warning.main">
                {stats.pendingOrders}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                In Production
              </Typography>
              <Typography variant="h4" color="primary.main">
                {stats.inProductionOrders}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Completed
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.completedOrders}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Revenue
              </Typography>
              <Typography variant="h4" color="success.main">
                KES {stats.totalRevenue.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending Payments
              </Typography>
              <Typography variant="h4" color="error.main">
                {stats.pendingPayments}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="order tabs">
          <Tab label="Orders" />
          <Tab label="Customers" />
          <Tab label="Products" />
          <Tab label="Analytics" />
        </Tabs>

        {/* Filters */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="in_production">In Production</MenuItem>
                  <MenuItem value="ready">Ready</MenuItem>
                  <MenuItem value="shipped">Shipped</MenuItem>
                  <MenuItem value="delivered">Delivered</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  label="Priority"
                >
                  <MenuItem value="all">All Priorities</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Payment</InputLabel>
                <Select
                  value={paymentFilter}
                  onChange={(e) => setPaymentFilter(e.target.value)}
                  label="Payment"
                >
                  <MenuItem value="all">All Payments</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="partial">Partial</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="refunded">Refunded</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3} sx={{ textAlign: 'right' }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => window.location.reload()}
                sx={{ mr: 1 }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialog('order')}
                disabled={!canManage('manager:orders') && !canManage('admin:orders')}
              >
                New Order
              </Button>
              <Button
                variant="outlined"
                startIcon={<Engineering />}
                disabled={creatingWO}
                onClick={async () => {
                  const firstOrder = filteredOrders[0];
                  if (!firstOrder) return;
                  try {
                    const result = await createWorkOrder({ orderId: firstOrder.id }).unwrap();
                    const wo = result as { id?: string };
                    if (wo?.id) navigate(`${basePath}/production/work-orders/${wo.id}`);
                  } catch {
                    setError('Failed to create work order. Ensure order exists in backend.');
                  }
                }}
                sx={{ borderColor: '#1b4d89', color: '#1b4d89' }}
              >
                Create Work Order
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Orders Tab */}
        <TabPanel value={tabValue} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Order #</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Production</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Payment</TableCell>
                  <TableCell>Expected Delivery</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {order.id.slice(0, 8)}…
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {order.orderNumber}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">
                          {order.customerName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {order.customerEmail}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {order.items.length} item(s)
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {order.items.map(item => item.productName).join(', ')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">
                        KES {order.totalAmount.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.status.replace('_', ' ')}
                        color={getStatusColor(order.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          order.status === 'in_production' ? 'in progress' :
                          order.status === 'confirmed' ? 'queued' :
                          order.status === 'shipped' ? 'dispatch' : 'pending'
                        }
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: '#1b4d89', color: '#1b4d89' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.priority}
                        color={getPriorityColor(order.priority) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Chip
                          label={order.paymentStatus}
                          color={order.paymentStatus === 'paid' ? 'success' : 'warning'}
                          size="small"
                        />
                        <Typography variant="caption" display="block">
                          {order.paymentMethod.toUpperCase()}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(order.expectedDelivery).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Order">
                          <IconButton size="small" onClick={() => setViewingOrder(order)}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Order">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog('order', order)}
                            disabled={!canManage('manager:orders') && !canManage('admin:orders')}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Update Status">
                          <IconButton
                            size="small"
                            onClick={() => {
                              const nextStatus = order.status === 'pending' ? 'confirmed' :
                                order.status === 'confirmed' ? 'in_production' :
                                order.status === 'in_production' ? 'ready' :
                                order.status === 'ready' ? 'shipped' :
                                order.status === 'shipped' ? 'delivered' : 'pending';
                              handleStatusChange(order.id, nextStatus);
                            }}
                            disabled={!canManage('manager:orders') && !canManage('admin:orders')}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Create Work Order">
                          <IconButton
                            size="small"
                            onClick={async () => {
                              try {
                                const result = await createWorkOrder({ orderId: order.id }).unwrap();
                                const wo = result as { id?: string };
                                if (wo?.id) navigate(`${basePath}/production/work-orders/${wo.id}`);
                              } catch {
                                setError('Failed to create work order for this order.');
                              }
                            }}
                          >
                            <Engineering />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Order">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete('order', order.id)}
                            disabled={!canManage('manager:orders') && !canManage('admin:orders')}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Customers Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 2, textAlign: 'right' }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog('customer')}
              disabled={!canManage('manager:orders') && !canManage('admin:orders')}
            >
              Add Customer
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Orders</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customers.map((customer) => {
                  const customerOrders = orders.filter(o => o.customerId === customer.id);
                  const totalSpent = customerOrders.reduce((sum, o) => sum + o.totalAmount, 0);
                  
                  return (
                    <TableRow key={customer.id}>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {customer.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{customer.email}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {customer.phone}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {customer.company}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {customerOrders.length} orders
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            KES {totalSpent.toLocaleString()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={customer.isActive ? 'Active' : 'Inactive'}
                          color={customer.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Edit Customer">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog('customer', customer)}
                              disabled={!canManage('manager:orders') && !canManage('admin:orders')}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Customer">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete('customer', customer.id)}
                              disabled={!canManage('manager:orders') && !canManage('admin:orders')}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Products Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ mb: 2, textAlign: 'right' }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog('product')}
              disabled={!canManage('manager:orders') && !canManage('admin:orders')}
            >
              Add Product
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {product.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {product.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={product.category} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">
                        KES {product.price.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={product.isActive ? 'Active' : 'Inactive'}
                        color={product.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit Product">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog('product', product)}
                            disabled={!canManage('manager:orders') && !canManage('admin:orders')}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Product">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete('product', product.id)}
                            disabled={!canManage('manager:orders') && !canManage('admin:orders')}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Analytics Tab */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Order Status Distribution" />
                <CardContent>
                  {['pending', 'confirmed', 'in_production', 'ready', 'shipped', 'delivered'].map(status => {
                    const count = orders.filter(o => o.status === status).length;
                    const percentage = orders.length > 0 ? (count / orders.length) * 100 : 0;
                    return (
                      <Box key={status} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {status.replace('_', ' ')}
                          </Typography>
                          <Typography variant="body2">
                            {count} ({percentage.toFixed(1)}%)
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          color={getStatusColor(status) as any}
                        />
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Revenue by Month" />
                <CardContent>
                  <Typography variant="h4" color="success.main" gutterBottom>
                    KES {stats.totalRevenue.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total revenue from all orders
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      Average order value: KES {(stats.totalRevenue / stats.totalOrders).toLocaleString()}
                    </Typography>
                    <Typography variant="body2">
                      Orders this month: {stats.totalOrders}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* View Order Dialog */}
      <Dialog open={!!viewingOrder} onClose={() => setViewingOrder(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Order Details — {viewingOrder?.orderNumber}</DialogTitle>
        <DialogContent>
          {viewingOrder && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2"><strong>ID:</strong> {viewingOrder.id}</Typography>
              <Typography variant="body2"><strong>Customer:</strong> {viewingOrder.customerName}</Typography>
              <Typography variant="body2"><strong>Status:</strong> {viewingOrder.status}</Typography>
              <Typography variant="body2"><strong>Total:</strong> KES {viewingOrder.totalAmount.toLocaleString()}</Typography>
              <Typography variant="body2"><strong>Items:</strong> {viewingOrder.items.length}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewingOrder(null)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (viewingOrder) {
                handleOpenDialog('order', viewingOrder);
                setViewingOrder(null);
              }
            }}
            disabled={!canManage('manager:orders') && !canManage('admin:orders')}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogs */}
      {/* Order Dialog */}
      <Dialog open={openDialog && dialogType === 'order'} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingOrder ? 'Edit Order' : 'Create New Order'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Customer</InputLabel>
                <Select
                  value={orderForm.customerId}
                  onChange={(e) => setOrderForm({ ...orderForm, customerId: e.target.value })}
                  label="Customer"
                  required
                >
                  {customers.map(customer => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.name} ({customer.company})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={orderForm.priority}
                  onChange={(e) => setOrderForm({ ...orderForm, priority: e.target.value as any })}
                  label="Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expected Delivery"
                type="date"
                value={orderForm.expectedDelivery}
                onChange={(e) => setOrderForm({ ...orderForm, expectedDelivery: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={orderForm.notes}
                onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : (editingOrder ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Customer Dialog */}
      <Dialog open={openDialog && dialogType === 'customer'} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingOrder ? 'Edit Customer' : 'Add New Customer'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={customerForm.name}
                onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company"
                value={customerForm.company}
                onChange={(e) => setCustomerForm({ ...customerForm, company: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={customerForm.email}
                onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={customerForm.phone}
                onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={2}
                value={customerForm.address}
                onChange={(e) => setCustomerForm({ ...customerForm, address: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : (editingOrder ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Dialog */}
      <Dialog open={openDialog && dialogType === 'product'} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingOrder ? 'Edit Product' : 'Add New Product'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={productForm.name}
                onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Price"
                type="number"
                value={productForm.price}
                onChange={(e) => setProductForm({ ...productForm, price: parseFloat(e.target.value) })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={productForm.category}
                  onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                  label="Category"
                >
                  <MenuItem value="clothing">Clothing</MenuItem>
                  <MenuItem value="accessories">Accessories</MenuItem>
                  <MenuItem value="furniture">Furniture</MenuItem>
                  <MenuItem value="electronics">Electronics</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : (editingOrder ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="new order"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => handleOpenDialog('order')}
        disabled={!canManage('manager:orders') && !canManage('admin:orders')}
      >
        <Add />
      </Fab>
    </Box>
  );
};
